import { Filter, TableRow } from './table-models';

export type CardTransactionOperation = 'SALE' | 'REFUND' | 'VOID';

export type CardTransactionOutcome =
    | 'APPROVED'
    | 'DECLINED'
    | 'FAILED'
    | 'ABORTED'
    | 'UNKNOWN'
    | 'UNPARSEABLE';

export const ALL_OUTCOMES: CardTransactionOutcome[] = [
    'APPROVED',
    'DECLINED',
    'FAILED',
    'ABORTED',
    'UNKNOWN',
    'UNPARSEABLE',
];

/**
 * Server-side audit row for a single card-payment attempt.
 * Mirrors `CardTransactionAttemptVO` in client-service.
 */
export interface CardTransactionAttempt extends TableRow {
    id: number;
    createdAt: string;
    durationMs?: number;

    invoiceId?: number;
    documentId?: number;
    userId?: number;
    branchId?: number;
    orderReference?: string;

    operation: CardTransactionOperation;
    outcome: CardTransactionOutcome;

    amountCents?: number;
    currency?: string;

    transactionKey?: string;
    mid?: string;
    tid?: string;
    stan?: string;
    rrn?: string;
    authCode?: string;
    cardMaskedPan?: string;
    cardType?: string;

    resultCode?: number;
    actionCode?: number;
    errorMessage?: string;

    responseBodyRaw?: string;
    requestPayloadRaw?: string;

    originalTransactionKey?: string;

    clientAppVersion?: string;
    deviceModel?: string;
    deviceManufacturer?: string;
    osVersion?: string;
    deviceId?: string;
    targetPackageName?: string;
}

export interface CardAttemptQueryParams {
    page?: number;
    itemsPerPage?: number;
    outcome?: CardTransactionOutcome;
    operation?: CardTransactionOperation;
    filterUserId?: number;
    deviceId?: string;
    search?: string;
    fromDate?: string;
    toDate?: string;
}

/**
 * `Filter.tags` is repurposed here to carry the multi-selected outcomes.
 * Backend takes a single `outcome` query param so we send the first selected one
 * for now; richer multi-select can come later via OR-clause server-side.
 */
export const toCardAttemptParams = (
    filter: Filter | undefined,
    page: number,
    itemsPerPage: number,
): CardAttemptQueryParams => {
    const out: CardAttemptQueryParams = { page, itemsPerPage };
    if (!filter) return out;
    if (filter.tags && filter.tags.length > 0) {
        out.outcome = filter.tags[0] as CardTransactionOutcome;
    }
    if (filter.search && filter.search.trim().length > 0) {
        out.search = filter.search.trim();
    }
    // Backend expects ISO_DATE_TIME on these endpoints; the date picker
    // produces ISO_DATE ("2026-05-12"). Pad start/end of day so the param
    // parses and the day-range comparison includes the full bound day.
    if (filter.after) out.fromDate = toIsoStartOfDay(filter.after);
    if (filter.before) out.toDate = toIsoEndOfDay(filter.before);
    if (filter.id) {
        const parsed = Number(filter.id);
        if (Number.isFinite(parsed)) out.filterUserId = parsed;
    }
    return out;
};

/**
 * Pads a date or date-time string to an ISO_DATE_TIME. Accepts:
 *   - "2026-05-12"               → "2026-05-12T00:00:00"
 *   - "2026-05-12T07:30"          → "2026-05-12T07:30:00"
 *   - "2026-05-12T07:30:45"       → unchanged
 *   - Date string (toLocaleString) → re-parsed via Date constructor
 * Anything unparseable falls through unchanged — backend will 400 cleanly
 * rather than silently dropping the filter.
 */
const toIsoStartOfDay = (value: string): string => normalizeDate(value, '00:00:00');
const toIsoEndOfDay = (value: string): string => normalizeDate(value, '23:59:59');

const normalizeDate = (value: string, padTime: string): string => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T${padTime}`;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return `${value}:00`;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) return value;
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${padTime}`;
    }
    return value;
};
